import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { components } from "@qorakul/shared/api";
import { api } from "./api.ts";
import { ApiError } from "./apiError.ts";

export type LeavingReason = components["schemas"]["LeavingReasonDetails"];
export type StudentExit = components["schemas"]["StudentExitDetails"];
export type ExitChecklistItem = components["schemas"]["ExitChecklistItemDetails"];
export type LeavingReasonCount = components["schemas"]["LeavingReasonCount"];

export const exitKeys = {
  reasons: ["leaving-reasons"] as const,
  counts: (from: string, to: string) => ["leaving-reasons", "counts", from, to] as const,
  exits: (studentId: string) => ["students", studentId, "exits"] as const,
};

/** The administrator-managed leaving reasons, active ones first. */
export function useLeavingReasons() {
  const { t } = useTranslation();
  return useQuery({
    queryKey: exitKeys.reasons,
    queryFn: async () => {
      const { data, error } = await api.GET("/leaving-reasons");
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
  });
}

/** The student's exit records, newest first. */
export function useStudentExits(studentId: string) {
  return useQuery({
    queryKey: exitKeys.exits(studentId),
    queryFn: async () => {
      const { data, error, response } = await api.GET("/students/{id}/exits", { params: { path: { id: studentId } } });
      if (!data) throw new ApiError(response.status, error);
      return data;
    },
  });
}
